import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import UserService from '../../../Services/user.service';

function validateMasavAccountantFields(t, subj, send, nm, benef, ser) {
    const tr = (s) => (s ?? '').toString().trim();
    const s = tr(subj);
    const sd = tr(send);
    const n = tr(nm);
    const b = tr(benef);
    const h = tr(ser);
    const allEmpty = !s && !sd && !n && !b && !h;
    if (allEmpty) return null;
    if (!s || !sd || !n || !b || !h) return t('MasavAccountantAllOrNone');
    if (!/^\d{8}$/.test(s)) return t('MasavValidationSubject8');
    if (!/^\d{5}$/.test(sd)) return t('MasavValidationSender5');
    if (n.length > 30) return t('MasavValidationName30');
    if (!/^\d{4}$/.test(b)) return t('MasavValidationBenef4');
    if (!/^\d{3}$/.test(h)) return t('MasavValidationSerial3');
    return null;
}

/**
 * Lets the accountant edit and save the five MASAV header fields (same rules as AccountantDetails).
 */
export default function MasavAccountantSettingsModal({ open, onClose, user, token, direction, onSaved }) {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';

    const [masavInstitutionSubjectCode, setMasavInstitutionSubjectCode] = useState('');
    const [masavSenderInstitutionCode, setMasavSenderInstitutionCode] = useState('');
    const [masavInstitutionName, setMasavInstitutionName] = useState('');
    const [masavBeneficiaryAccountType, setMasavBeneficiaryAccountType] = useState('');
    const [masavHeaderSerial, setMasavHeaderSerial] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !user) return;
        setMasavInstitutionSubjectCode(user.masavInstitutionSubjectCode ?? '');
        setMasavSenderInstitutionCode(user.masavSenderInstitutionCode ?? '');
        setMasavInstitutionName(user.masavInstitutionName ?? '');
        setMasavBeneficiaryAccountType(user.masavBeneficiaryAccountType ?? '');
        setMasavHeaderSerial(user.masavHeaderSerial ?? '');
    }, [open, user]);

    const hasNoMasavSettings =
        !masavInstitutionSubjectCode.trim() &&
        !masavSenderInstitutionCode.trim() &&
        !masavInstitutionName.trim() &&
        !masavBeneficiaryAccountType.trim() &&
        !masavHeaderSerial.trim();

    const handleSave = async () => {
        if (!user?.accountantId || !token) {
            toast.error(t('Error'));
            return;
        }
        const masavErr = validateMasavAccountantFields(
            t,
            masavInstitutionSubjectCode,
            masavSenderInstitutionCode,
            masavInstitutionName,
            masavBeneficiaryAccountType,
            masavHeaderSerial
        );
        if (masavErr) {
            toast.error(masavErr);
            return;
        }
        setSaving(true);
        try {
            const editedItem = {
                AccountantId: user.accountantId,
                PhoneNumber: user.phoneNumber,
                Name: user.name,
                Email: user.email,
                PricePerHour: user.pricePerHour != null ? parseFloat(String(user.pricePerHour), 10) : null,
                MasavInstitutionSubjectCode: masavInstitutionSubjectCode.trim() || null,
                MasavSenderInstitutionCode: masavSenderInstitutionCode.trim() || null,
                MasavInstitutionName: masavInstitutionName.trim() || null,
                MasavBeneficiaryAccountType: masavBeneficiaryAccountType.trim() || null,
                MasavHeaderSerial: masavHeaderSerial.trim() || null,
            };
            const response = await UserService.EditUser(token, editedItem);
            if (response && response.ok) {
                const updated = await UserService.UpdateUser(user.accountantId, token);
                toast.success(t('Accounting data has been updated successfully'));
                onSaved?.(updated);
            } else {
                const errText = response ? await response.text().catch(() => '') : '';
                toast.error(errText ? `${t('Error on save new accountant data')}: ${errText}` : t('Error on save new accountant data'));
            }
        } catch (e) {
            console.error(e);
            toast.error(t('Error on save new accountant data'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !saving && onClose?.()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ dir: direction, sx: { borderRadius: 2 } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    gap: 1,
                }}
            >
                <Typography variant="h6" component="span">
                    {t('MasavAccountantSettingsModalTitle')}
                </Typography>
                <IconButton onClick={() => !saving && onClose?.()} size="small" aria-label={t('Close')}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {hasNoMasavSettings && (
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 700 }}>
                        {t('MasavCompleteSettingsBeforeContinue')}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('MasavAccountantSettingsModalHint')}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 2,
                        direction,
                        textAlign: isRtl ? 'right' : 'left',
                    }}
                >
                    <TextField
                        label={t('MasavInstitutionSubjectCodeLabel')}
                        value={masavInstitutionSubjectCode}
                        onChange={(e) => setMasavInstitutionSubjectCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        size="small"
                        placeholder="12345678"
                        inputProps={{ maxLength: 8, inputMode: 'numeric' }}
                        disabled={saving}
                    />
                    <TextField
                        label={t('MasavSenderInstitutionCodeLabel')}
                        value={masavSenderInstitutionCode}
                        onChange={(e) => setMasavSenderInstitutionCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        size="small"
                        placeholder="12345"
                        inputProps={{ maxLength: 5, inputMode: 'numeric' }}
                        disabled={saving}
                    />
                    <TextField
                        label={t('MasavInstitutionNameMasavLabel')}
                        value={masavInstitutionName}
                        onChange={(e) => setMasavInstitutionName(e.target.value.slice(0, 30))}
                        size="small"
                        inputProps={{ maxLength: 30 }}
                        disabled={saving}
                    />
                    <TextField
                        label={t('MasavBeneficiaryAccountTypeLabel')}
                        value={masavBeneficiaryAccountType}
                        onChange={(e) => setMasavBeneficiaryAccountType(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        size="small"
                        placeholder="0000"
                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                        disabled={saving}
                    />
                    <TextField
                        label={t('MasavHeaderSerialLabel')}
                        value={masavHeaderSerial}
                        onChange={(e) => setMasavHeaderSerial(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        size="small"
                        placeholder="001"
                        inputProps={{ maxLength: 3, inputMode: 'numeric' }}
                        disabled={saving}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, flexDirection: 'row', gap: 1 }}>
                <Button onClick={() => !saving && onClose?.()} color="inherit" disabled={saving}>
                    {t('Cancel')}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}>
                    {t('Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
